import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Home, Users, ShoppingCart, Box, Users2, Settings, Calendar } from "lucide-react";
import useLanguageChange from "../../Components/Hooks/useLanguageChange";

function SidebarMenu({ items, openMenus, toggleMenu, location, level = 0, isCollapsed }) {
  return (
    <>
      {items.map((item) =>
        item.subItems ? (
          <li key={item.label} className="relative">
            <button
              className={`flex items-center w-full px-4 py-2 rounded hover:bg-green-50 transition justify-between ${
                openMenus[level] === item.label ? "bg-green-100 text-green-700" : "text-gray-700"
              }`}
              onClick={() => toggleMenu(item.label, level)}
            >
              <span className="flex items-center gap-2">
                {item.icon && <span className="mr-0 md:mr-2">{item.icon}</span>}
                <span
                  className={`inline-block transition-all duration-300 whitespace-nowrap overflow-hidden ${
                    isCollapsed ? 'opacity-0 max-w-0 ml-0' : 'opacity-100 max-w-xs ml-2'
                  }`}
                  style={{ minWidth: 0 }}
                >
                  {item.label}
                </span>
              </span>
              {!isCollapsed && (
                openMenus[level] === item.label ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )
              )}
            </button>
            <ul
              className={`pl-8 pr-2 py-1 transition-all duration-200 overflow-hidden ${
                openMenus[level] === item.label && !isCollapsed ? "max-h-96" : "max-h-0"
              }`}
            >
              <SidebarMenu
                items={item.subItems}
                openMenus={openMenus}
                toggleMenu={toggleMenu}
                location={location}
                level={level + 1}
                isCollapsed={isCollapsed}
              />
            </ul>
          </li>
        ) : (
          <li key={item.link}>
            <Link
              to={item.link}
              className={`flex items-center px-4 py-2 rounded hover:bg-green-50 transition ${
                location.pathname === item.link
                  ? "bg-green-100 text-green-700 font-semibold"
                  : "text-gray-700"
              }`}
            >
              {item.icon && <span className="mr-0 md:mr-2">{item.icon}</span>}
              <span
                className={`inline-block transition-all duration-300 whitespace-nowrap overflow-hidden ${
                  isCollapsed ? 'opacity-0 max-w-0 ml-0' : 'opacity-100 max-w-xs ml-2'
                }`}
                style={{ minWidth: 0 }}
              >
                {item.label}
              </span>
            </Link>
          </li>
        )
      )}
    </>
  );
}

export default function SidebarContent({ isCollapsed }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { forceUpdate } = useLanguageChange(); // Force re-render when language changes
  // openMenus: { [level]: label } => chỉ 1 menu expand ở mỗi cấp
  const [openMenus, setOpenMenus] = useState({});

  // Grouped menu structure with translation
  const menu = [
    { label: t("Dashboard"), link: "/dashboard", icon: <Home size={20} /> },
    { label: t("Orders"), link: "/orders", icon: <ShoppingCart size={20} /> },
    { label: t("Calendar"), link: "/calendar", icon: <Calendar size={20} /> },
    {
      label: t("Management"),
      icon: <Box size={20} />,
      subItems: [
        { label: t("Products"), link: "/products", icon: <Box size={20} /> },
        { label: t("Customers"), link: "/customers", icon: <Users2 size={20} /> },
        { label: t("Users"), link: "/users", icon: <Users size={20} /> },
      ],
    },
    { label: t("Settings"), link: "/settings", icon: <Settings size={20} /> },
  ];

  const toggleMenu = (label, level) => {
    setOpenMenus((prev) => ({
      ...prev,
      [level]: prev[level] === label ? null : label,
    }));
  };

  return (
    <SidebarMenu
      key={`sidebar-${forceUpdate}`} // Force re-render when language changes
      items={menu}
      openMenus={openMenus}
      toggleMenu={toggleMenu}
      location={location}
      isCollapsed={isCollapsed}
    />
  );
}
